import React, { useEffect, useState, useRef } from "react";
import { Row, Col } from "react-bootstrap";
import { ActionHandlerFunction, IActionHandler, PaginationItem, Sort, SortDirection } from "@preveil-api";
import {
  Account, WatchLogSortFieldType, SORT_DIRECTION, WatchedLogsEntity, WatchedLogEntity, getOppositeSortDirection, sortBy, ActivityLogFilters, DriveWatchedMessages, GenericEntryEntity,
  PanelStateType, PanelState, NodePathInfoMap, PathInfo
} from "src/common";
import { Loading, Icon } from "src/components";
import { EmptyMessage, SidePanel } from "..";
import { ListRow } from ".";
import _ from "lodash";

type AllProps = {
    current_account: Account;
    is_loading: boolean;
    watched_activity: WatchedLogsEntity;
    filter?: ActivityLogFilters;
    pagination: PaginationItem;
    handleAction: ActionHandlerFunction;
}

function ListViewComponent(props: AllProps) {
    const { is_loading, current_account, filter, watched_activity, handleAction } = props;
    const [entries, setEntries] = useState<WatchedLogEntity[]>(watched_activity.entries);
    const [detail_entry, setDetailEntry] = useState<GenericEntryEntity | undefined>();
    const [panel_state, setPanelState] = useState<PanelStateType>(PanelState.off);
    const [sort, setSort] = useState<Sort<keyof WatchedLogEntity>>({ field: WatchLogSortFieldType.last_modification_date, direction: SORT_DIRECTION.descending });

    // Description: Maintainer nodes fetched grants
    const path_list_ref = useRef<NodePathInfoMap>(new Map<string, PathInfo[]>);
    function setPathListRef(id: string, _path_list_ref: PathInfo[]) {
        path_list_ref.current.set(id, _path_list_ref);
    }

    useEffect(() => {
        // NOTE: Set filtered and sorted entries after fetching watched_list
        if (!!watched_activity.entries) {
            !!filter?.name_filter ? handleFilter() : handleSort(sort.field, sort.direction, watched_activity.entries);
        };
    }, [watched_activity]);


    useEffect(() => {
        handleFilter();
    }, [filter?.name_filter]);

    // Description: Filter entries by name ( NOTE: Server side Filter by User and date )
    function handleFilter() {
        if (!!filter) {
            let filtered = watched_activity.entries.slice();
            if (!!filter.name_filter) {
                const _search_term = filter.name_filter.toLowerCase();
                filtered = _.filter(watched_activity.entries, (entry: WatchedLogEntity) => entry.name.toLowerCase().includes(_search_term));
            }
            handleSort(sort.field, sort.direction, filtered);
        } else {
            handleSort(sort.field, sort.direction, watched_activity.entries);
        }
    }

    // Description: Sets the sort field with the new field. (if its the same field, it flips the direction)
    function handleSort(field: keyof WatchedLogEntity, _direction?: SortDirection, sort_entries: WatchedLogEntity[] = entries) {
        const direction = !!_direction ? _direction :
            (sort.field === field ? getOppositeSortDirection(sort.direction) : SORT_DIRECTION.ascending);
        setSort({ field, direction });

        if (!!sort_entries) {
            const sorted_entries = sortBy(sort_entries, { field, direction }, field === WatchLogSortFieldType.last_modification_date);
            setEntries(sorted_entries);
        }
    }

    const ListViewRequests = {
        // Description: Set Location info
        handleSetPathInfo(params: { id: string, path_info: PathInfo[] }) {
            setPathListRef(params.id, params.path_info);
        },

        //  Description: Handle view details from menu
        handleDetails(entry: GenericEntryEntity) {
            setDetailEntry(entry);
            setPanelState(PanelState.on);
        },

        // Description: Toggle side panel used for close and reset, but can be used for open
        handleToggleSidePanel(_panel_state: PanelStateType) {
            setPanelState(_panel_state);
            if (_panel_state === PanelState.off) { // Reset states
                setTimeout(() => {
                    setDetailEntry(undefined);
                }, 500);
            }
        }
    };

    //  Description: Handle all actions from Children forms
    function handlePageActions(action_obj: IActionHandler) {
        const callback = `handle${action_obj.actionType}`;
        if ((ListViewRequests as any)[callback] instanceof Function) {
            (ListViewRequests as any)[callback](action_obj.params);
        } else {
            // NOTE: If not found here propagate to parent
            handleAction(action_obj);
        }
    }

    // Render methods
    // ------------------------------------------------
    // Descriptions: Shareable react fragment - for each field - returns the header with sort icon
    function headerWithSortIcon(name: string, field: string) {
        return (
            <>
                {name}
                {!!sort && sort.field === field && !!entries && entries.length > 1 && (
                    <Icon className={`${sort.direction === SORT_DIRECTION.descending ? "ficon-arrow-down" : "ficon-arrow-up"} small-icon ms-1`} />
                )}
            </>
        );
    }

    // Description: Render Header component
    function RenderHeader() {
        return <Row className="header-row">
            <Col md="3" className="col" onClick={() => handleSort(WatchLogSortFieldType.name)}>
                {headerWithSortIcon("Name", WatchLogSortFieldType.name)}
            </Col>
            <Col md="2" onClick={() => handleSort(WatchLogSortFieldType.event)}>
                {headerWithSortIcon("Event", WatchLogSortFieldType.event)}
            </Col>
            <Col md="3" onClick={() => handleSort(WatchLogSortFieldType.user_id)}>
                {headerWithSortIcon("User", WatchLogSortFieldType.user_id)}
            </Col>
            <Col md="3" onClick={() => handleSort(WatchLogSortFieldType.last_modification_date)}>
                {headerWithSortIcon("Modified", WatchLogSortFieldType.last_modification_date)}
            </Col>
            <Col md="1">
                <span hidden>actions</span>
            </Col>
        </Row>;
    }

    // Description: Render the rows
    function RenderList() {
        return <div className="list-body">
            {
                _.map(entries, (entry: WatchedLogEntity, i: number) => {
                    return <ListRow
                        key={`item_${i}`}
                        current_account={current_account}
                        entry={entry}
                        handleAction={handlePageActions} />;
                })
            }
        </div>;
    }
    return <div className={`drive-list watched-logs${is_loading ? " isloading" : ""}`}>
        {
            watched_activity.entries.length > 0 ?
                <>
                    {RenderHeader()}
                    {RenderList()}
                </> :
                <EmptyMessage message={DriveWatchedMessages.watched_changes_empty} />
        }
        {
            is_loading && <Loading />
        }
        {
            !!detail_entry &&
            <SidePanel
                current_account={current_account}
                panel_state={panel_state}
                entry={detail_entry}
                path_info={path_list_ref.current.get(detail_entry.id)}
                handleAction={handlePageActions} />
        }
    </div>;
}

export default React.memo(ListViewComponent);